import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLongPressAction } from "../hooks/useLongPressAction";
import {
  QUICK_MENU_FAVORITE_IDS,
  resolveQuickMenuFavorites,
} from "../helpers/quickMenuFavorites";
import {
  ASHLEY_LAUNCH_GESTURE,
  isAshleyLongPressLaunchGuardActive,
  launchAshleyInterface,
  releaseAshleyLongPressLaunchGuards,
} from "../helpers/ashleyInterfaceLauncher";
import { useAshleyInterface } from "../ashley/AshleyInterfaceProvider";
import { useUserPreferencesOptional } from "../context/UserPreferencesContext";
import {
  dismissGuidedWorkflow,
  isGuidedWorkflowActive,
} from "../ashley/forms/guidedWorkflowRegistry";
import { useGuidedModeOptional } from "../productEducation/shared/TourEngine";
import { GUIDED_MODE_SCOPES } from "../productEducation/shared/TourEngine/TourEngine";
import { fabTouchLog } from "./fabTouchDebug";
import {
  markAshleyUserGesture,
  voiceDiag,
} from "../ashley/debug/ashleyVoiceDiagnostics";
import "./FloatingHomeButton.css";

import PayrollIcon from "../assets/icons/payroll.svg";
import EstimatesIcon from "../assets/icons/estimates.svg";
import TimeTrackingIcon from "../assets/icons/Time_Tracking.png";
import TimeLogsIcon from "../assets/icons/time_logs_icon.png";
import CreateProjectIcon from "../assets/icons/CreateProject.svg";
import ashleyGuideIcon from "../assets/mission-control/ashley-guide.png";

const QUICK_MENU_ICONS = {
  [QUICK_MENU_FAVORITE_IDS.PAYROLL]: PayrollIcon,
  [QUICK_MENU_FAVORITE_IDS.ESTIMATES]: EstimatesIcon,
  [QUICK_MENU_FAVORITE_IDS.TIME_TRACKING]: TimeTrackingIcon,
  [QUICK_MENU_FAVORITE_IDS.TIME_LOGS]: TimeLogsIcon,
  [QUICK_MENU_FAVORITE_IDS.CREATE_PROJECT]: CreateProjectIcon,
};

const RADIAL_ANCHOR_STYLE = {
  position: "fixed",
  bottom: "40px",
  right: "40px",
  width: "80px",
  height: "80px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10000,
  cursor: "pointer",
};

const QUICK_MENU_AUTO_CLOSE_MS = 2000;
const TAP_DEBOUNCE_MS = 400;
const LONG_PRESS_LAUNCH_RELEASE_GUARD_MS = 450;
const DOUBLE_TAP_INTERVAL_MS = 300;
const QUICK_MENU_NAV_LONG_PRESS_COOLDOWN_MS = 1000;

export function FloatingHomeButton({
  onOpenModeSelect,
  onOpenDashboard,
  onOpenEstimates,
  onOpenPayrollQueue,
  onOpenCommandCenter,
  onOpenTimeLogs,
  onOpenCreateProject,
  favoriteIds = null,
  hidden,
  showPayrollQuick = true,
  showEstimatesQuick = true,
  showTimeLogsQuick = true,
  showCreateProjectQuick = true,
}) {
  const [open, setOpen] = useState(false);
  const [menuAutoCloseTimer, setMenuAutoCloseTimer] = useState(null);
  const [missionRecording, setMissionRecording] = useState(false);

  const longPressHandledRef = useRef(false);
  const longPressLaunchPendingReleaseRef = useRef(false);
  const tapActivateGuardRef = useRef(false);
  const singleTapTimerRef = useRef(null);
  const lastTapAtRef = useRef(0);
  const longPressCooldownUntilRef = useRef(0);
  const isLongPressBlockedRef = useRef(() => false);
  isLongPressBlockedRef.current = () => Date.now() < longPressCooldownUntilRef.current;
  const guidedMode = useGuidedModeOptional();
  const { isActive: isAshleyVoiceActive, closeAshleyInterface } = useAshleyInterface();
  const userPreferences = useUserPreferencesOptional();
  const voiceFirstNavigation = userPreferences?.isVoiceFirstNavigation ?? true;
  const suspendAutoDismiss = Boolean(
    guidedMode?.hasScope?.(GUIDED_MODE_SCOPES.SUSPEND_AUTO_DISMISS),
  );

  const closeQuickMenu = () => {
    if (menuAutoCloseTimer) {
      clearTimeout(menuAutoCloseTimer);
      setMenuAutoCloseTimer(null);
    }
    setOpen(false);
  };

  const openQuickMenu = () => {
    voiceDiag("Quick menu opened (long press)", {
      hypothesisId: "H-LP",
      location: "FloatingHomeButton.openQuickMenu",
    });

    setOpen(true);

    if (menuAutoCloseTimer) {
      clearTimeout(menuAutoCloseTimer);
      setMenuAutoCloseTimer(null);
    }

    if (!suspendAutoDismiss) {
      const autoCloseTimer = setTimeout(() => {
        setOpen(false);
      }, QUICK_MENU_AUTO_CLOSE_MS);

      setMenuAutoCloseTimer(autoCloseTimer);
    }

    window.dispatchEvent(new CustomEvent("quick-menu-opened"));
    window.dispatchEvent(new CustomEvent("mission-control-opened"));
  };

  useEffect(() => {
    if (suspendAutoDismiss || !open) {
      return undefined;
    }

    const autoCloseTimer = setTimeout(() => {
      setOpen(false);
      setMenuAutoCloseTimer(null);
    }, QUICK_MENU_AUTO_CLOSE_MS);

    setMenuAutoCloseTimer(autoCloseTimer);

    return () => {
      clearTimeout(autoCloseTimer);
    };
  }, [suspendAutoDismiss, open]);

  useEffect(
    () => () => {
      if (singleTapTimerRef.current) {
        clearTimeout(singleTapTimerRef.current);
      }
    },
    [],
  );

  const cancelPendingSingleTap = () => {
    if (singleTapTimerRef.current) {
      clearTimeout(singleTapTimerRef.current);
      singleTapTimerRef.current = null;
    }
  };

  const openQuickMenuFromTap = () => {
    tapActivateGuardRef.current = true;
    setTimeout(() => {
      tapActivateGuardRef.current = false;
    }, TAP_DEBOUNCE_MS);

    voiceDiag("FAB tap → openQuickMenu() (quick-menu-first)", {
      hypothesisId: "H-WIRE",
      location: "FloatingHomeButton.openQuickMenuFromTap",
    });
    openQuickMenu();
  };

  const navigateToKpiDashboard = () => {
    cancelPendingSingleTap();
    lastTapAtRef.current = 0;
    closeQuickMenu();

    tapActivateGuardRef.current = true;
    setTimeout(() => {
      tapActivateGuardRef.current = false;
    }, TAP_DEBOUNCE_MS);

    voiceDiag("FAB double tap → KPI Dashboard (quick-menu-first)", {
      hypothesisId: "H-WIRE",
      location: "FloatingHomeButton.navigateToKpiDashboard",
    });
    onOpenDashboard?.();
  };

  const handleQuickMenuFirstTap = () => {
    const now = Date.now();

    if (lastTapAtRef.current && now - lastTapAtRef.current <= DOUBLE_TAP_INTERVAL_MS) {
      navigateToKpiDashboard();
      return;
    }

    lastTapAtRef.current = now;
    cancelPendingSingleTap();
    singleTapTimerRef.current = setTimeout(() => {
      singleTapTimerRef.current = null;
      lastTapAtRef.current = 0;
      openQuickMenuFromTap();
    }, DOUBLE_TAP_INTERVAL_MS);
  };

  const activateVoiceAssistant = (gestureSource = "fab-tap") => {
    if (isGuidedWorkflowActive()) {
      markAshleyUserGesture(gestureSource);
      voiceDiag("FAB → dismissGuidedWorkflow()", {
        hypothesisId: "H-WIRE",
        location: "FloatingHomeButton.activateVoiceAssistant",
      });
      dismissGuidedWorkflow();
      closeQuickMenu();
      tapActivateGuardRef.current = true;
      setTimeout(() => {
        tapActivateGuardRef.current = false;
      }, TAP_DEBOUNCE_MS);
      return;
    }

    if (isAshleyVoiceActive) {
      markAshleyUserGesture(gestureSource);
      voiceDiag("FAB → closeAshleyInterface()", {
        hypothesisId: "H-WIRE",
        location: "FloatingHomeButton.activateVoiceAssistant",
      });
      closeAshleyInterface();
      closeQuickMenu();
      return;
    }

    tapActivateGuardRef.current = true;
    setTimeout(() => {
      tapActivateGuardRef.current = false;
    }, TAP_DEBOUNCE_MS);

    markAshleyUserGesture(gestureSource);
    voiceDiag("FAB → launchAshleyInterface()", {
      hypothesisId: "H-WIRE",
      location: "FloatingHomeButton.activateVoiceAssistant",
    });
    launchAshleyInterface(
      gestureSource === "fab-long-press"
        ? { launchGesture: ASHLEY_LAUNCH_GESTURE.LONG_PRESS }
        : undefined,
    );
    closeQuickMenu();
  };

  const releaseLongPressLaunchGuardsAfterPointerUp = () => {
    if (!longPressLaunchPendingReleaseRef.current) {
      return;
    }

    longPressLaunchPendingReleaseRef.current = false;
    window.setTimeout(() => {
      releaseAshleyLongPressLaunchGuards();
    }, LONG_PRESS_LAUNCH_RELEASE_GUARD_MS);
  };

  const activateAshleyInterface = () => {
    fabTouchLog("onPress");

    if (longPressHandledRef.current) {
      longPressHandledRef.current = false;
      return;
    }

    if (isAshleyLongPressLaunchGuardActive()) {
      return;
    }

    if (tapActivateGuardRef.current) {
      return;
    }

    if (missionRecording) {
      console.log("MISSION RECORDING STOPPED");
      setMissionRecording(false);
      window.dispatchEvent(new CustomEvent("mission-recording-stopped"));
      return;
    }

    if (voiceFirstNavigation) {
      activateVoiceAssistant("fab-tap");
      return;
    }

    handleQuickMenuFirstTap();
  };

  const { ref: longPressTouchTargetRef, cancelActiveGesture } = useLongPressAction({
    onLongPress: () => {
      fabTouchLog("onLongPress");

      if (missionRecording) {
        return;
      }

      if (Date.now() < longPressCooldownUntilRef.current) {
        voiceDiag("Long press ignored — post-navigation cooldown", {
          hypothesisId: "H-LP",
          location: "FloatingHomeButton.onLongPress",
        });
        return;
      }

      cancelPendingSingleTap();
      lastTapAtRef.current = 0;
      longPressHandledRef.current = true;

      if (voiceFirstNavigation) {
        voiceDiag("Long press detected — opening Quick Menu", {
          hypothesisId: "H-LP",
          location: "FloatingHomeButton.onLongPress",
        });
        openQuickMenu();
        return;
      }

      voiceDiag("Long press detected — launching Voice Assistant (quick-menu-first)", {
        hypothesisId: "H-LP",
        location: "FloatingHomeButton.onLongPress",
      });
      longPressLaunchPendingReleaseRef.current = true;
      activateVoiceAssistant("fab-long-press");
    },
    enabled: true,
    preventDefaultOnStart: false,
    isLongPressBlockedRef,
  });

  const terminateQuickMenuGesture = useCallback(() => {
    cancelPendingSingleTap();
    lastTapAtRef.current = 0;
    longPressLaunchPendingReleaseRef.current = false;
    releaseAshleyLongPressLaunchGuards();
    cancelActiveGesture();
    longPressCooldownUntilRef.current = Date.now() + QUICK_MENU_NAV_LONG_PRESS_COOLDOWN_MS;

    // Swallow release/click events still in flight from the gesture that opened the menu.
    longPressHandledRef.current = true;
    tapActivateGuardRef.current = true;

    voiceDiag("Quick menu navigation — FAB gesture lifecycle terminated", {
      hypothesisId: "H-LP",
      location: "FloatingHomeButton.terminateQuickMenuGesture",
    });
  }, [cancelActiveGesture]);

  const favorites = useMemo(
    () =>
      resolveQuickMenuFavorites(favoriteIds, {
        payroll: showPayrollQuick,
        estimates: showEstimatesQuick,
        timeLogs: showTimeLogsQuick,
        createProject: showCreateProjectQuick,
      }),
    [
      favoriteIds,
      showPayrollQuick,
      showEstimatesQuick,
      showTimeLogsQuick,
      showCreateProjectQuick,
    ],
  );

  const handleFavoriteSelect = (favoriteId) => {
    terminateQuickMenuGesture();
    closeQuickMenu();

    if (favoriteId === QUICK_MENU_FAVORITE_IDS.PAYROLL) {
      onOpenPayrollQueue();
      return;
    }
    if (favoriteId === QUICK_MENU_FAVORITE_IDS.ESTIMATES) {
      onOpenEstimates();
      return;
    }
    if (favoriteId === QUICK_MENU_FAVORITE_IDS.TIME_TRACKING) {
      window.dispatchEvent(new CustomEvent("command-center-opened"));
      onOpenCommandCenter();
      return;
    }
    if (favoriteId === QUICK_MENU_FAVORITE_IDS.TIME_LOGS) {
      onOpenTimeLogs?.();
      return;
    }
    if (favoriteId === QUICK_MENU_FAVORITE_IDS.CREATE_PROJECT) {
      onOpenCreateProject();
    }
  };

  const handleAllModules = () => {
    terminateQuickMenuGesture();
    closeQuickMenu();
    onOpenModeSelect?.();
  };

  const handlePressIn = () => {
    fabTouchLog("onPressIn");
    longPressHandledRef.current = false;
    tapActivateGuardRef.current = false;
  };

  const handlePressOut = () => {
    fabTouchLog("onPressOut");
    releaseLongPressLaunchGuardsAfterPointerUp();
  };

  if (hidden) return null;

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeQuickMenu}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.25)",
                zIndex: 9998,
              }}
            />

            <motion.div
              className="guide-quick-menu"
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "fixed",
                bottom: 12,
                right: 12,
                width: 268,
                height: 304,
                pointerEvents: "none",
                zIndex: 9997,
              }}
            />

            {favorites.map((favorite) => (
              <motion.div
                key={favorite.id}
                className={favorite.guideClassName}
                initial={{
                  opacity: 0,
                  scale: 0,
                  x: 0,
                  y: 0,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  ...favorite.animate,
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={favorite.transition}
                style={RADIAL_ANCHOR_STYLE}
                onClick={() => handleFavoriteSelect(favorite.id)}
              >
                <img
                  src={QUICK_MENU_ICONS[favorite.id]}
                  alt={favorite.alt}
                  className={favorite.iconClassName}
                  style={{
                    width: "80px",
                    height: "80px",
                    opacity: favorite.iconOpacity,
                    pointerEvents: "none",
                  }}
                />
              </motion.div>
            ))}

            <motion.div
              className="quick-menu-all-modules-anchor"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.button
                type="button"
                className="quick-menu-all-modules guide-all-modules ios-interaction-guard"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25, delay: 0.1 }}
                onClick={handleAllModules}
              >
                All Modules
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <button
        type="button"
        className={`ashley-fab floating-home-button guide-ashley-fab ios-interaction-guard${
          isAshleyVoiceActive ? " ashley-fab--voice-active" : ""
        }`}
        aria-label="Ashley"
        onClick={activateAshleyInterface}
      >
        <span
          ref={longPressTouchTargetRef}
          className="ashley-fab-touch-target"
          aria-hidden="true"
          onPointerDown={handlePressIn}
          onPointerUp={handlePressOut}
          onPointerCancel={handlePressOut}
          onTouchEnd={handlePressOut}
          onTouchCancel={handlePressOut}
        />
        <img
          src={ashleyGuideIcon}
          alt=""
          aria-hidden="true"
          className="ashley-fab-visual"
          draggable={false}
        />
      </button>
    </>
  );
}
